/**
 * Alpine.js Status & Post Composer Component
 * Unified component for creating/editing status updates (140 chars) and posts (1000 chars with photos)
 */

import { editorBase } from './alpine-editor-base.js';
import { uploadPhoto, parseUploadError, validateFile, PhotoUploadConfig } from './photo-uploader.js';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';
import { createDatePickerMixin } from './trip-date-picker.js';
import { SM_BREAKPOINT } from './breakpoints.js';

// Helper to get translation (falls back to key if i18n not available)
function t(key, params) {
    return window.t ? window.t(key, params) : key;
}

/**
 * Status & Post Composer Component Factory
 * Handles dual-mode editing: quick status updates or full posts with photos
 * Also supports editing existing posts
 */
export function statusPostComposer() {
    return {
        // Shared date picker mixin (provides showDatePicker, datePickerStep, datePickerMonth,
        // datePickerYear, getMonthYearDisplay, prevMonth, nextMonth, formatDateISO, formatDateShort,
        // resetDatePicker; getCalendarDays and selectDate are overridden below for trip field names)
        ...createDatePickerMixin(),

        // Mode configuration
        MODES: {
            STATUS: 'status',
            POST: 'post',
            TRIP: 'trip'
        },

        LIMITS: {
            STATUS: 140,
            POST: 1000,
            TRIP: 500
        },

        ENDPOINTS: {
            STATUS: '/app/helpers/status',
            POST_API: '/v1/post',
            TRIP_API: '/v1/trip'
        },

        // Visibility options - labels are computed via getter to use translations
        VISIBILITY_VALUES: ['public', 'users', 'friends', 'me'],

        MAX_PHOTOS: 10,
        MAX_FILE_SIZE: PhotoUploadConfig.MAX_FILE_SIZE,
        ALLOWED_TYPES: PhotoUploadConfig.ALLOWED_TYPES,
        CHAR_LIMIT_WARNING_THRESHOLD: 0.9,
        MAX_TEXTAREA_HEIGHT_POST: 200,
        MAX_TEXTAREA_HEIGHT_STATUS: 96,
        UPLOAD_CHECK_INTERVAL_MS: 500,
        UPLOAD_TIMEOUT_MS: 60000,

        // Reactive state
        mode: 'post',
        content: '',
        title: '',
        savedStatus: '',
        // Per-mode content storage to preserve data when switching modes
        postContent: '',
        tripContent: '',
        statusContent: '',
        photos: [],
        photosById: new Map(),
        visibility: 'public',
        isSaving: false,
        isSubmittingPost: false,
        showSuccess: false,
        successMessage: '',
        error: null,
        userId: null,
        username: null,
        showStatusDisplay: false,
        uploadControllers: new Map(),
        uploadCheckInterval: null,
        uploadTimeout: null,

        // Collapsed/expanded state
        isExpanded: false,
        mobileSheetOpen: false,

        // Trip mode state
        tripCityId: null,
        tripCityName: '',
        tripCountryCode: null,
        tripStartDate: null,
        tripEndDate: null,
        tripLookingFor: [],
        tripType: null,

        // Edit mode state
        isEditMode: false,
        editPostId: null,
        editFeedOwnerId: null,
        photosToDelete: [],
        showTitleField: false,
        onCancelCallback: null,
        onCompleteCallback: null,

        // Computed properties
        get isMobile() {
            return window.innerWidth < SM_BREAKPOINT;
        },

        get isStatusMode() {
            return this.mode === this.MODES.STATUS;
        },

        get isPostMode() {
            return this.mode === this.MODES.POST;
        },

        get isTripMode() {
            return this.mode === this.MODES.TRIP;
        },

        get maxLength() {
            if (this.isStatusMode) return this.LIMITS.STATUS;
            if (this.isTripMode) return this.LIMITS.TRIP;
            return this.LIMITS.POST;
        },

        get charCount() {
            return this.content.length;
        },

        get isNearLimit() {
            return this.charCount > this.maxLength * this.CHAR_LIMIT_WARNING_THRESHOLD;
        },

        get hasContent() {
            return this.content.trim().length > 0;
        },

        get hasPhotos() {
            return this.photos.length > 0;
        },

        get canSave() {
            if (this.isSaving || this.isSubmittingPost) return false;
            if (this.isPostMode) {
                // Don't allow submission if any photos failed to upload
                if (this.failedPhotosCount > 0) {
                    return false;
                }
                return this.hasContent || this.hasPhotos; // Posts require content OR photos
            }
            if (this.isTripMode) {
                // Trip requires destination and dates
                return this.tripCityId && this.tripStartDate && this.tripEndDate;
            }
            return true; // Status can be empty (to clear)
        },

        get uploadingPhotosCount() {
            return this.photos.filter(p => p.uploading).length;
        },

        get failedPhotosCount() {
            return this.photos.filter(p => p.error).length;
        },

        get showFooter() {
            return this.isExpanded;
        },

        get placeholder() {
            // Reference $store.i18n.ready to re-compute when translations load
            void this.$store?.i18n?.ready;
            if (this.isPostMode) return t('common.post_placeholder_default');
            if (this.isTripMode) return t('common.post_placeholder_trip');
            return t('common.post_placeholder_status');
        },

        get tripDateDisplay() {
            void this.$store?.i18n?.ready;
            if (!this.tripStartDate) return t('common.post_trip_when_button');
            const start = this.formatDate(this.tripStartDate);
            if (!this.tripEndDate) return `${start} - ?`;
            const end = this.formatDate(this.tripEndDate);
            return `${start} - ${end}`;
        },

        get hasTripDates() {
            return this.tripStartDate && this.tripEndDate;
        },

        get visibilityOptions() {
            void this.$store?.i18n?.ready;
            return [
                { value: 'public', label: t('common.post_visibility_all_users') },
                { value: 'users', label: t('common.post_visibility_members') },
                { value: 'friends', label: t('common.post_visibility_my_friends') },
                { value: 'me', label: t('common.post_visibility_only_me') }
            ];
        },

        get currentVisibilityLabel() {
            const option = this.visibilityOptions.find(o => o.value === this.visibility);
            return option ? option.label : t('common.post_visibility_all_users');
        },

        get saveButtonText() {
            void this.$store?.i18n?.ready;
            if (this.showSuccess) return t('common.post_saved_success');
            if (this.isSaving) return t('common.post_saving');
            if (this.isSubmittingPost) {
                if (this.uploadingPhotosCount > 0) {
                    const key = this.uploadingPhotosCount > 1 ? 'common.post_uploading_photos_plural' : 'common.post_uploading_photos';
                    return t(key, { count: this.uploadingPhotosCount });
                }
                return this.isEditMode ? t('common.post_updating') : t('common.post_posting');
            }
            if (this.isEditMode) return t('common.post_button_update');
            if (this.isStatusMode) return t('common.post_button_update');
            if (this.isTripMode) return t('common.post_share_trip');
            return t('common.post_button_post');
        },

        // Methods
        init(userId, username, initialStatus = '', config = {}) {
            // Alpine.js automatically calls init() without params if the method exists.
            // Skip initialization if called without required parameters - the x-init
            // directive will call this method again with proper values.
            if (!userId || !username) {
                return;
            }

            this.userId = userId;
            this.username = username;
            this.savedStatus = initialStatus || '';

            // Configure edit mode if provided
            if (config.editMode) {
                this.isEditMode = true;
                this.editPostId = config.postId;
                this.editFeedOwnerId = config.feedOwnerId;
                this.content = config.message || '';
                this.title = config.title || '';
                this.visibility = config.visibility || 'public';
                this.showTitleField = !!config.title || config.showTitleField;
                this.onCancelCallback = config.onCancel || null;
                this.onCompleteCallback = config.onComplete || null;

                // Load existing photos if provided
                if (config.photos && config.photos.length > 0) {
                    this.loadExistingPhotos(config.photos);
                }

                // Force post mode for editing
                this.mode = this.MODES.POST;
            }

            // Show status display if we have a saved status and are in status mode
            if (this.isStatusMode && this.savedStatus) {
                this.showStatusDisplay = true;
            }

            // Auto-resize after content is set
            this.$nextTick(() => {
                this.autoResizeTextarea();
                // Run again after a short delay to ensure Alpine has fully hydrated
                setTimeout(() => {
                    this.autoResizeTextarea();
                }, 50);
            });

            // Watch content changes for auto-resize
            this.$watch('content', () => {
                this.$nextTick(() => {
                    this.autoResizeTextarea();
                });
            });

            // Close mobile sheet if viewport resizes to desktop
            this._resizeHandler = () => {
                if (this.mobileSheetOpen && !this.isMobile) {
                    this.mobileSheetOpen = false;
                    unlockBodyScroll();
                }
            };
            window.addEventListener('resize', this._resizeHandler);
        },

        loadExistingPhotos(photos) {
            // Convert existing post photos to the format expected by the component
            // Photo URLs are already constructed in the backend, just use them directly
            // IMPORTANT: Keep photo IDs as strings to preserve precision
            this.photos = photos.map((photo, index) => ({
                id: String(photo.photo_id) || Date.now() + index,
                uploadedPhotoId: String(photo.photo_id),
                thumbnailUrl: photo.url_medium || photo.url_small || photo.url,
                dataUrl: null,
                uploading: false,
                error: null,
                isExisting: true,
                file: null
            }));

            // Populate photosById map
            this.photos.forEach(photo => {
                this.photosById.set(photo.id, photo);
            });
        },

        toggleTitleField() {
            this.showTitleField = !this.showTitleField;
            if (!this.showTitleField) {
                this.title = '';
            }
        },

        // Expand/collapse methods
        expand(targetMode = null) {
            this.isExpanded = true;
            if (this.isMobile && !this.isEditMode && !this.mobileSheetOpen) {
                this.mobileSheetOpen = true;
                lockBodyScroll();
            }
            if (targetMode && targetMode !== this.mode) {
                this.switchMode(targetMode);
            } else if (targetMode === 'trip' || this.mode === 'trip') {
                // Initialize autocomplete if expanding directly into trip mode
                this.initTripCityAutocomplete();
            }
            this.$nextTick(() => {
                const textarea = this.$refs.textarea;
                if (textarea) {
                    textarea.focus();
                }
                // Re-initialize Lucide icons in the expanded container
                window.LucideIcons?.initialize(this.$el);
            });
        },

        collapse() {
            // Only collapse if there's no content to preserve
            const hasTripData = this.tripCityId || this.tripStartDate || this.tripEndDate;
            if (!this.hasContent && !this.hasPhotos && !hasTripData) {
                this.isExpanded = false;
                if (this.mobileSheetOpen) {
                    this.mobileSheetOpen = false;
                    unlockBodyScroll();
                }
            }
        },

        closeMobileSheet() {
            this.isExpanded = false;
            if (this.mobileSheetOpen) {
                this.mobileSheetOpen = false;
                unlockBodyScroll();
            }
        },

        handleClickOutside(event) {
            // On mobile, backdrop handles closing instead
            if (this.mobileSheetOpen) return;
            // Don't collapse if there's any user-entered data
            const hasTripData = this.tripCityId || this.tripStartDate || this.tripEndDate;
            if (!this.hasContent && !this.hasPhotos && !hasTripData) {
                this.isExpanded = false;
            }
        },

        // Date formatting helper
        formatDate(dateStr) {
            if (!dateStr) return '';
            // Parse YYYY-MM-DD as local date (not UTC) to avoid timezone shift
            const [year, month, day] = dateStr.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        },

        // Trip-specific methods
        toggleTripLookingFor(item) {
            const index = this.tripLookingFor.indexOf(item);
            if (index === -1) {
                this.tripLookingFor.push(item);
            } else {
                this.tripLookingFor.splice(index, 1);
            }
        },

        hasTripLookingFor(item) {
            return this.tripLookingFor.includes(item);
        },

        selectTripType(type) {
            this.tripType = this.tripType === type ? null : type;
        },

        clearTripData() {
            this.tripCityId = null;
            this.tripCityName = '';
            this.tripCountryCode = null;
            this.tripStartDate = null;
            this.tripEndDate = null;
            this.tripLookingFor = [];
            this.tripType = null;
            this.resetDatePicker();
        },

        clearTripDates() {
            this.tripStartDate = null;
            this.tripEndDate = null;
            this.datePickerStep = 'start';
            this.showDatePicker = false;
        },

        // Date picker methods
        openDatePicker() {
            const ref = this.tripStartDate ? new Date(this.tripStartDate + 'T00:00:00') : new Date();
            this.datePickerMonth = ref.getMonth();
            this.datePickerYear = ref.getFullYear();
            this.datePickerStep = this.tripStartDate ? 'end' : 'start';
            this.showDatePicker = true;
        },

        // Override mixin's getCalendarDays to use tripStartDate/tripEndDate
        getCalendarDays() {
            const days = [];
            const firstDay = new Date(this.datePickerYear, this.datePickerMonth, 1);
            const lastDay = new Date(this.datePickerYear, this.datePickerMonth + 1, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            for (let i = 0; i < firstDay.getDay(); i++) {
                days.push({ empty: true });
            }

            for (let d = 1; d <= lastDay.getDate(); d++) {
                const date = new Date(this.datePickerYear, this.datePickerMonth, d);
                const dateStr = this.formatDateISO(date);
                const isPast = date < today;
                const isBeforeStart = this.datePickerStep === 'end' &&
                    this.tripStartDate && dateStr < this.tripStartDate;

                days.push({
                    day: d,
                    dateStr,
                    isToday: date.getTime() === today.getTime(),
                    isStart: dateStr === this.tripStartDate,
                    isEnd: dateStr === this.tripEndDate,
                    isInRange: this.tripStartDate && this.tripEndDate &&
                        dateStr > this.tripStartDate && dateStr < this.tripEndDate,
                    disabled: isPast || isBeforeStart
                });
            }

            return days;
        },

        // Override mixin's selectDate to use tripStartDate/tripEndDate
        selectDate(dateStr) {
            if (this.datePickerStep === 'start') {
                this.tripStartDate = dateStr;
                if (this.tripEndDate && this.tripEndDate < dateStr) {
                    this.tripEndDate = null;
                }
                this.datePickerStep = 'end';
            } else {
                this.tripEndDate = dateStr;
                this.showDatePicker = false;
            }
        },

        setTripCity(cityId, cityName, countryCode = null) {
            this.tripCityId = String(cityId);
            this.tripCityName = cityName;
            this.tripCountryCode = countryCode;
        },

        initTripCityAutocomplete() {
            // Wait for DOM to update
            this.$nextTick(() => {
                const input = this.$refs.tripCityInput;
                if (!input) return;

                const $input = $(input);

                // Check if already initialized
                if ($input.data('easyAutocomplete')) return;

                // Check if EasyAutocomplete is available
                if (typeof $input.easyAutocomplete !== 'function') {
                    console.error('EasyAutocomplete not available');
                    return;
                }

                // Check if genCityAcOptions is available
                if (typeof window.genCityAcOptions !== 'function') {
                    console.error('genCityAcOptions not available');
                    return;
                }

                // Store reference to Alpine component
                const self = this;

                // Get the base options from genCityAcOptions
                const acOptions = window.genCityAcOptions($input, {
                    minCharNumber: 1
                });

                // Override the list.onChooseEvent to capture selection
                const originalOnChoose = acOptions.list?.onChooseEvent;
                acOptions.list = acOptions.list || {};
                acOptions.list.onChooseEvent = function() {
                    // Get the selected item data
                    const selectedItem = $input.getSelectedItemData();
                    if (selectedItem && selectedItem.id) {
                        self.setTripCity(selectedItem.id, $input.val(), selectedItem.country_iso || null);
                    }
                    // Call original handler if exists
                    if (originalOnChoose) {
                        originalOnChoose.call(this);
                    }
                    // Close dropdown after selection
                    const container = $input.next('.easy-autocomplete-container');
                    container.find('ul').hide();
                    input.blur();
                };

                // Initialize autocomplete
                $input.easyAutocomplete(acOptions);
            });
        },

        switchMode(newMode) {
            const prevMode = this.mode;
            if (prevMode === newMode) return;

            this.error = null;

            // Save current mode's content before switching
            if (prevMode === this.MODES.POST) {
                this.postContent = this.content;
            } else if (prevMode === this.MODES.TRIP) {
                this.tripContent = this.content;
            } else if (prevMode === this.MODES.STATUS) {
                this.statusContent = this.content;
            }

            this.mode = newMode;

            // Restore new mode's content
            if (this.isPostMode) {
                this.content = this.postContent;
                this.showStatusDisplay = false;
            } else if (this.isTripMode) {
                this.content = this.tripContent;
                this.showStatusDisplay = false;
                // Initialize city autocomplete
                this.initTripCityAutocomplete();
            } else {
                // Status mode
                if (this.savedStatus && !this.statusContent) {
                    this.showStatusDisplay = true;
                    this.content = this.savedStatus;
                } else {
                    this.showStatusDisplay = false;
                    this.content = this.statusContent || '';
                }
            }

            this.$nextTick(() => {
                this.autoResizeTextarea();
                window.LucideIcons?.initialize(this.$el);
            });
        },

        editStatus() {
            this.showStatusDisplay = false;
            this.content = this.savedStatus;

            this.$nextTick(() => {
                this.autoResizeTextarea();
                const textarea = this.$refs.textarea;
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                }
            });
        },

        handleInput() {
            this.$nextTick(() => {
                this.autoResizeTextarea();
            });
        },

        handleKeydown(event) {
            // Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) to submit
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                if (this.canSave) {
                    this.save();
                }
            }
        },

        autoResizeTextarea() {
            const textarea = this.$refs.textarea;
            if (!textarea) return;

            textarea.style.height = 'auto';
            const maxHeight = this.isPostMode ? this.MAX_TEXTAREA_HEIGHT_POST : this.MAX_TEXTAREA_HEIGHT_STATUS;
            const newHeight = Math.min(textarea.scrollHeight, maxHeight);
            textarea.style.height = newHeight + 'px';
        },

        async handlePhotoSelect(event) {
            const files = Array.from(event.target.files);

            // Validate photo count
            if (this.photos.length + files.length > this.MAX_PHOTOS) {
                this.error = t('common.post_max_photos_error', { count: this.MAX_PHOTOS });
                event.target.value = '';
                return;
            }

            for (const file of files) {
                // Validate file using shared validator
                const validation = validateFile(file);
                if (!validation.valid) {
                    this.error = `${file.name}: ${validation.error}`;
                    event.target.value = '';
                    return;
                }

                // Create photo object with preview
                const reader = new FileReader();
                const photoId = Date.now() + Math.random();

                reader.onload = async (e) => {
                    // Add photo with preview and uploading state
                    const photoObj = {
                        file: file,
                        dataUrl: e.target.result,
                        id: photoId,
                        uploading: true,
                        uploadedPhotoId: null,
                        thumbnailUrl: null,
                        progress: 0,
                        error: null
                    };

                    this.photos.push(photoObj);
                    this.photosById.set(photoId, photoObj);

                    // Start upload immediately using shared uploader
                    await this.doUploadPhoto(photoId, file);
                };
                reader.readAsDataURL(file);
            }

            // Clear input to allow selecting same file again
            event.target.value = '';
        },

        async doUploadPhoto(photoId, file) {
            // Create abort controller for this upload
            const controller = new AbortController();
            this.uploadControllers.set(photoId, controller);

            try {
                const photoData = await uploadPhoto({
                    file,
                    controller,
                    onProgress: (percent) => {
                        this.updatePhotoState(photoId, { progress: percent });
                    }
                });

                // Update photo with uploaded data
                this.updatePhotoState(photoId, {
                    uploading: false,
                    uploadedPhotoId: String(photoData.id),
                    thumbnailUrl: photoData.thumb_medium || photoData.thumb_large || photoData.url_small,
                    progress: 100,
                    error: null
                });
            } catch (error) {
                // Mark photo as failed
                const errorMsg = parseUploadError(error);
                this.updatePhotoState(photoId, {
                    uploading: false,
                    error: errorMsg !== 'Upload cancelled' ? errorMsg : null
                });
            } finally {
                this.uploadControllers.delete(photoId);
            }
        },

        updatePhotoState(photoId, updates) {
            const photo = this.photosById.get(photoId);
            if (!photo) return;

            Object.assign(photo, updates);

            // Trigger reactivity by creating new array reference
            this.photos = [...this.photos];
        },

        removePhoto(photoId) {
            // Abort in-progress upload if any
            const controller = this.uploadControllers.get(photoId);
            if (controller) {
                controller.abort();
                this.uploadControllers.delete(photoId);
            }

            const photo = this.photosById.get(photoId);

            // Track existing photos for deletion on save
            if (photo && photo.isExisting && photo.uploadedPhotoId) {
                this.photosToDelete.push(photo.uploadedPhotoId);
            }

            this.photos = this.photos.filter(p => p.id !== photoId);
            this.photosById.delete(photoId);
        },

        retryUpload(photoId) {
            const photo = this.photosById.get(photoId);
            if (!photo || !photo.file) return;

            // Reset photo state
            this.updatePhotoState(photoId, {
                uploading: true,
                progress: 0,
                error: null
            });

            // Retry upload using shared uploader
            this.doUploadPhoto(photoId, photo.file);
        },

        cycleVisibility() {
            const currentIndex = this.VISIBILITY_VALUES.indexOf(this.visibility);
            const nextIndex = (currentIndex + 1) % this.VISIBILITY_VALUES.length;
            this.visibility = this.VISIBILITY_VALUES[nextIndex];
        },

        clear() {
            this.content = '';
            this.title = '';
            // Clear per-mode content for current mode only
            if (this.isPostMode) {
                this.postContent = '';
            } else if (this.isTripMode) {
                this.tripContent = '';
            } else if (this.isStatusMode) {
                this.statusContent = '';
            }
            this.photos = [];
            this.photosById.clear();
            this.photosToDelete = [];
            this.error = null;
            this.$nextTick(() => {
                this.autoResizeTextarea();
            });
        },

        async save() {
            if (!this.canSave) return;

            this.isSaving = true;
            this.error = null;

            try {
                if (this.isTripMode) {
                    await this.saveTrip();
                } else if (this.isStatusMode) {
                    await this.saveStatus();
                } else {
                    if (this.isEditMode) {
                        await this.updatePost();
                    } else {
                        await this.savePost();
                    }
                }
            } catch (error) {
                this.error = error.message || t('common.post_save_failed');
                console.error('Save error:', error);
            } finally {
                this.isSaving = false;
            }
        },

        async saveStatus() {
            const csrfToken = editorBase.getCsrfToken();

            const formData = new FormData();
            formData.append('status_msg', this.content.trim());
            if (csrfToken) {
                formData.append('csrf_token', csrfToken);
            }

            const response = await fetch(this.ENDPOINTS.STATUS, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(t('common.post_save_failed'));
            }

            // Update saved status
            this.savedStatus = this.content.trim();

            // Close mobile sheet and collapse
            if (this.mobileSheetOpen) {
                this.mobileSheetOpen = false;
                this.isExpanded = false;
                unlockBodyScroll();
            }

            // Show success message
            if (this.savedStatus) {
                this.showToast(t('common.post_status_updated'));
                this.showStatusDisplay = true;
            } else {
                this.showToast(t('common.post_status_cleared'));
                this.showStatusDisplay = false;
            }
        },

        async saveTrip() {
            // Get auth token
            const token = await window.getApiToken();
            if (!token) {
                throw new Error(t('common.post_auth_failed'));
            }

            // Build trip data (dates are already YYYY-MM-DD from the date picker)
            const data = {
                city_id: this.tripCityId,
                start_date: this.tripStartDate,
                end_date: this.tripEndDate,
                view_acl: this.visibility === 'public' ? 0 :
                          this.visibility === 'users' ? 1 :
                          this.visibility === 'friends' ? 2 : 3
            };

            // Add description if provided
            if (this.content.trim()) {
                data.description = this.content.trim();
            }

            // Add looking_for tags if any selected
            if (this.tripLookingFor.length > 0) {
                data.looking_for = JSON.stringify(this.tripLookingFor);
            }

            if (this.tripType) {
                data.trip_type = this.tripType;
            }

            const response = await fetch(this.ENDPOINTS.TRIP_API, {
                method: 'POST',
                headers: {
                    'X-AUTH-TOKEN': token,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                },
                body: new URLSearchParams(data),
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                // Extract error message from API response format
                let errorMessage = 'Failed to create trip';
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.errors) {
                    // API returns errors object with param -> {code, message}
                    const firstError = Object.values(errorData.errors)[0];
                    if (firstError?.message) {
                        errorMessage = firstError.message;
                    } else if (firstError?.code) {
                        errorMessage = `Invalid ${Object.keys(errorData.errors)[0]}`;
                    }
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
                throw new Error(errorMessage);
            }

            // Clear form and reset state
            this.clear();
            this.clearTripData();
            this.isExpanded = false;
            if (this.mobileSheetOpen) {
                this.mobileSheetOpen = false;
                unlockBodyScroll();
            }

            // Show success and redirect to user's trips page
            this.showToast(t('common.post_trip_shared'));

            setTimeout(() => {
                window.location.href = '/app/trips?u=' + encodeURIComponent(this.username);
            }, 500);
        },

        async savePost() {
            // Check if any photos failed to upload
            const failedPhotos = this.photos.filter(p => p.error);
            if (failedPhotos.length > 0) {
                throw new Error(t('common.post_photos_failed'));
            }

            // Check if any photos are still uploading
            const uploadingPhotos = this.photos.filter(p => p.uploading);
            if (uploadingPhotos.length > 0) {
                // Instead of throwing error, set submitting state and wait for uploads
                this.isSubmittingPost = true;
                this.error = null;

                // Wait for all uploads to complete
                await this.waitForUploadsToComplete();

                // Re-check for failures or still-uploading photos after waiting
                const failedAfterWait = this.photos.filter(p => p.error);
                const stillUploadingAfterWait = this.photos.filter(p => p.uploading);

                if (failedAfterWait.length > 0) {
                    this.isSubmittingPost = false;
                    throw new Error(t('common.post_photos_failed'));
                }

                if (stillUploadingAfterWait.length > 0) {
                    this.isSubmittingPost = false;
                    throw new Error(t('common.post_upload_timeout'));
                }
            }

            // Get auth token (using global function from getApiToken.js)
            const token = await window.getApiToken();
            if (!token) {
                this.isSubmittingPost = false;
                throw new Error(t('common.post_auth_failed'));
            }

            // Collect already-uploaded photo IDs
            const photoIds = this.photos
                .filter(p => p.uploadedPhotoId)
                .map(p => p.uploadedPhotoId);

            // Create post
            const result = await this.createPost(token, photoIds);

            // Clear form
            this.isSubmittingPost = false;
            this.clear();
            if (this.mobileSheetOpen) {
                this.mobileSheetOpen = false;
                unlockBodyScroll();
            }

            // Navigate to posts section - force reload if already on the same page
            const targetUrl = '/' + encodeURIComponent(this.username) + '#posts';
            const currentPath = window.location.pathname;
            const targetPath = '/' + encodeURIComponent(this.username);

            if (currentPath === targetPath || currentPath === targetPath + '/') {
                window.location.reload();
            } else {
                window.location.href = targetUrl;
            }
        },

        async waitForUploadsToComplete() {
            return new Promise((resolve) => {
                this.uploadCheckInterval = setInterval(() => {
                    const stillUploading = this.photos.filter(p => p.uploading);
                    if (stillUploading.length === 0) {
                        this.clearUploadTimers();
                        resolve();
                    }
                }, this.UPLOAD_CHECK_INTERVAL_MS);

                this.uploadTimeout = setTimeout(() => {
                    this.clearUploadTimers();
                    resolve();
                }, this.UPLOAD_TIMEOUT_MS);
            });
        },

        clearUploadTimers() {
            if (this.uploadCheckInterval) {
                clearInterval(this.uploadCheckInterval);
                this.uploadCheckInterval = null;
            }
            if (this.uploadTimeout) {
                clearTimeout(this.uploadTimeout);
                this.uploadTimeout = null;
            }
        },

        destroy() {
            // Clean up timers on component destroy
            this.clearUploadTimers();

            // Release scroll lock if mobile sheet was open
            if (this.mobileSheetOpen) {
                this.mobileSheetOpen = false;
                unlockBodyScroll();
            }

            // Remove resize listener
            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
                this._resizeHandler = null;
            }

            // Abort any in-progress uploads
            this.uploadControllers.forEach(controller => controller.abort());
            this.uploadControllers.clear();
        },

        async createPost(token, photoIds) {
            const data = {
                message: this.content.trim(),
                view_acl: this.visibility,
                feed_type: 'user',
                feed_owner_id: 'self'
            };

            if (this.title.trim()) {
                data.title = this.title.trim();
            }

            if (photoIds.length > 0) {
                data.photo_ids = photoIds.join(',');
            }

            const response = await fetch(this.ENDPOINTS.POST_API, {
                method: 'POST',
                headers: {
                    'X-AUTH-TOKEN': token,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                },
                body: new URLSearchParams(data),
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || t('common.post_save_failed'));
            }

            return await response.json();
        },

        async updatePost() {
            // Check if any photos failed to upload
            const failedPhotos = this.photos.filter(p => p.error);
            if (failedPhotos.length > 0) {
                throw new Error(t('common.post_photos_failed'));
            }

            // Check if any photos are still uploading
            const uploadingPhotos = this.photos.filter(p => p.uploading);
            if (uploadingPhotos.length > 0) {
                this.isSubmittingPost = true;
                this.error = null;

                await this.waitForUploadsToComplete();

                const failedAfterWait = this.photos.filter(p => p.error);
                const stillUploadingAfterWait = this.photos.filter(p => p.uploading);

                if (failedAfterWait.length > 0) {
                    this.isSubmittingPost = false;
                    throw new Error(t('common.post_photos_failed'));
                }

                if (stillUploadingAfterWait.length > 0) {
                    this.isSubmittingPost = false;
                    throw new Error(t('common.post_upload_timeout'));
                }
            }

            const token = await window.getApiToken();
            if (!token) {
                this.isSubmittingPost = false;
                throw new Error(t('common.post_auth_failed'));
            }

            // Collect ALL photo IDs that should remain on the post (existing + newly uploaded)
            // The API will diff this against current photos:
            // - Photos in new list but not in old = add
            // - Photos in old list but not in new = delete
            const allPhotoIds = this.photos
                .filter(p => p.uploadedPhotoId)
                .map(p => p.uploadedPhotoId);

            // Build update payload
            const data = {
                message: this.content.trim(),
                view_acl: this.visibility
            };

            if (this.title.trim()) {
                data.title = this.title.trim();
            }

            // Send all photo IDs that should be on the post
            // API will diff against existing to determine adds/deletes
            if (allPhotoIds.length > 0) {
                data.photo_ids = allPhotoIds.join(',');
            } else if (this.photosToDelete.length > 0) {
                // Empty string explicitly removes all photos
                data.photo_ids = '';
            }

            const response = await fetch(`${this.ENDPOINTS.POST_API}/${this.editPostId}`, {
                method: 'PUT',
                headers: {
                    'X-AUTH-TOKEN': token,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                },
                body: new URLSearchParams(data),
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || t('common.post_save_failed'));
            }

            this.isSubmittingPost = false;
            this.showToast(t('common.post_updated_success'));

            // Call completion callback if provided (for inline editing)
            if (this.onCompleteCallback) {
                setTimeout(() => {
                    if (typeof this.onCompleteCallback === 'function') {
                        this.onCompleteCallback();
                    } else if (typeof this.onCompleteCallback === 'string') {
                        // Execute parent Alpine method by name
                        this.$dispatch('post-updated');
                    }
                }, 300);
            } else {
                // Default: redirect to post display page
                setTimeout(() => {
                    window.location.href = `/app/${this.editFeedOwnerId}/posts/${this.editPostId}`;
                }, 500);
            }
        },

        showToast(message) {
            // Show success state on the submit button instead of floating toast
            this.successMessage = message;
            this.showSuccess = true;

            // Reset after 2 seconds
            setTimeout(() => {
                this.showSuccess = false;
                this.successMessage = '';
            }, 2000);
        }
    };
}
